import { AdvancedMarker, APIProvider, Map as GoogleMap, useMap } from '@vis.gl/react-google-maps';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost } from '../../api-client'
import ResetSidebar from '../../components/Common/ResetSidebar';
import { SafeCombobox } from '../../components/Common/SafeCombobox';
import { collectLineItemNames } from './collectLineItemNames';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import './visits-map.css';

const EMPTY_JOB_ID_SET = new Set();

const HORIZON_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'today-tomorrow', label: 'Today or Tomorrow' },
  { value: 'week', label: 'Next Week' },
  { value: 'month', label: 'Next Month' },
];

const DEFAULT_FILTERS = {
  horizon: 'today',
  includeTags: [],
  excludeTags: [],
  postalCode: '',
  address: '',
  lineItemName: '',
};

function buildFilterParams(filters) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      const joined = value.map((entry) => entry?.id ?? entry).filter(Boolean).join(',');
      if (joined !== '') params.set(key, joined);
    } else if (value && value.trim() !== '') {
      params.set(key, value.trim());
    }
  });
  return params.toString();
}

function FitMapToMarkers({ points }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    if (!points.length) {
      map.setCenter({ lat: 45.5017, lng: -73.5673 });
      map.setZoom(11);
      return;
    }

    if (points.length === 1) {
      map.setCenter(points[0]);
      map.setZoom(13);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds, 60);
  }, [map, points]);

  return null;
}

const JobMarker = memo(function JobMarker({ id, lat, lng, clientName, selectionClassName, onToggle }) {
  const position = useMemo(() => ({ lat, lng }), [lat, lng]);
  const handleClick = useCallback(() => onToggle(id), [id, onToggle]);

  return (
    <AdvancedMarker
      position={position}
      title={clientName}
      clickable
      onClick={handleClick}
      anchorLeft="-50%"
      anchorTop="-100%"
    >
      <div className={`visits-map-marker ${selectionClassName}`} aria-label={clientName}>
        <span className="visits-map-marker__dot" />
      </div>
    </AdvancedMarker>
  )
});

const JobsMap = memo(function JobsMap({ markerPositions, styledJobIdSet, styledJobClassName, onToggleJob }) {
  return (
    <GoogleMap
      style={{ width: '100%', height: "70vh" }}
      defaultCenter={{ lat: 45.5017, lng: -73.5673 }}
      defaultZoom={11}
      mapId={process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID'}
    >
      <FitMapToMarkers points={markerPositions} />
      {markerPositions.map(({ id, lat, lng, clientName }) => {
        const selectionClassName = styledJobIdSet.has(id) ? styledJobClassName : '';

        return (
          <JobMarker
            key={id}
            id={id}
            lat={lat}
            lng={lng}
            clientName={clientName}
            selectionClassName={selectionClassName}
            onToggle={onToggleJob}
          />
        )
      })}
    </GoogleMap>
  )
}, (previousProps, nextProps) => {
  if (
    previousProps.markerPositions !== nextProps.markerPositions
    || previousProps.onToggleJob !== nextProps.onToggleJob
    || previousProps.styledJobIdSet !== nextProps.styledJobIdSet
  ) {
    return false;
  }

  return previousProps.styledJobIdSet.size === 0
    || previousProps.styledJobClassName === nextProps.styledJobClassName;
});

const JobListItem = memo(function JobListItem({ job, selectionClassName, onToggle }) {
  const handleClick = useCallback(() => onToggle(job.jobId), [job.jobId, onToggle]);

  return (
    <button
      type="button"
      className={`visits-map-list__item text-start ${selectionClassName}`}
      onClick={handleClick}
    >
      <p className='m-0 text-wrap'>{job.client?.name} - #{job?.jobNumber} {job.title}
      </p>
      {job.nextVisit && (
        <small className='d-block'>
          Next visit: {new Date(job.nextVisit.startAt).toLocaleString()}
        </small>
      )}
    </button>
  )
});

const JobsList = memo(function JobsList({ jobs, styledJobIdSet, styledJobClassName, selectedCount, selectableJobCount, onToggleJob, onSelectAllJobs, onClearSelectedJobs }) {
  const hasJobs = selectableJobCount > 0;
  const hasSelection = selectedCount > 0;
  const isAllSelected = hasJobs && selectedCount === selectableJobCount;

  return (
    <div className='visits-map-list overflow-y-scroll d-flex flex-column'>
      <div className='visits-map-list__header'>
        <span className='visits-map-list__select-label'>Select</span>
        <button
          type="button"
          className='visits-map-list__button'
          disabled={!hasJobs || isAllSelected}
          onClick={onSelectAllJobs}
        >
          All
        </button>
        {" | "}
        <button
          type="button"
          className='visits-map-list__button'
          disabled={!hasSelection}
          onClick={onClearSelectedJobs}
        >
          None
        </button>
      </div>
      {!hasJobs && (
        <p className='p-3 text-muted mb-0'>No jobs match the current filters.</p>
      )}
      {jobs.map(job => {
        const selectionClassName = styledJobIdSet.has(job.jobId) ? styledJobClassName : '';

        return (
          <JobListItem
            key={job.jobId}
            job={job}
            selectionClassName={selectionClassName}
            onToggle={onToggleJob}
          />
        )
      })}
    </div>
  )
}, (previousProps, nextProps) => {
  if (
    previousProps.jobs !== nextProps.jobs
    || previousProps.selectedCount !== nextProps.selectedCount
    || previousProps.selectableJobCount !== nextProps.selectableJobCount
    || previousProps.onToggleJob !== nextProps.onToggleJob
    || previousProps.onSelectAllJobs !== nextProps.onSelectAllJobs
    || previousProps.onClearSelectedJobs !== nextProps.onClearSelectedJobs
    || previousProps.styledJobIdSet !== nextProps.styledJobIdSet
  ) {
    return false;
  }

  return previousProps.styledJobIdSet.size === 0
    || previousProps.styledJobClassName === nextProps.styledJobClassName;
});

function FilterSelect({ label, name, value, allLabel, options, onChange, disabled }) {
  return (
    <label className='visits-map-filters__field'>
      <span>{label}</span>
      <select
        name={name}
        className='form-select'
        value={value}
        onChange={onChange}
        disabled={disabled}
      >
        <option value=''>{allLabel}</option>
        {options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    </label>
  );
}

function FilterBar({ filters, options, lineItemOptions, onChange, onApply, onRefresh, isFetching }) {
  const handleSelectChange = useCallback((event) => {
    const { name, value } = event.target;
    onApply({ ...filters, [name]: value });
  }, [filters, onApply]);

  const handleTextChange = useCallback((event) => {
    const { name, value } = event.target;
    onChange((currentFilters) => ({ ...currentFilters, [name]: value }));
  }, [onChange]);

  const handleSubmit = useCallback((event) => {
    event.preventDefault();
    onApply(filters);
  }, [filters, onApply]);

  const applyTextFilters = useCallback(() => onApply(filters), [filters, onApply]);

  return (
    <form className='visits-map-filters' onSubmit={handleSubmit}>
      <span className='visits-map-filters__lead'>Filter recurring jobs</span>
      <label className='visits-map-filters__field'>
        <span>with no incomplete visits before</span>
        <select
          name='horizon'
          className='form-select'
          value={filters.horizon}
          onChange={handleSelectChange}
        >
          {HORIZON_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
      <label className='visits-map-filters__field'>
        <span>and postal code contains</span>
        <input
          type='text'
          name='postalCode'
          className='form-control'
          value={filters.postalCode}
          onChange={handleTextChange}
          onBlur={applyTextFilters}
        />
      </label>
      <FilterSelect
        label='with a line item of'
        name='lineItemName'
        value={filters.lineItemName}
        allLabel='All'
        options={lineItemOptions}
        onChange={handleSelectChange}
      />
      <SafeCombobox
        multiSelect
        label='and client is tagged'
        selected={filters.includeTags}
        onSelect={(selection) => onApply({ ...filters, includeTags: selection })}
      >
        {(options?.tags || []).map((tag) => (
          <SafeCombobox.Option key={tag} id={tag} label={tag} />
        ))}
      </SafeCombobox>
      <SafeCombobox
        multiSelect
        label='and client is not tagged'
        selected={filters.excludeTags}
        onSelect={(selection) => onApply({ ...filters, excludeTags: selection })}
      >
        {(options?.tags || []).map((tag) => (
          <SafeCombobox.Option key={tag} id={tag} label={tag} />
        ))}
      </SafeCombobox>
      <button
        type='button'
        className='btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1'
        onClick={onRefresh}
        disabled={isFetching}
      >
        {isFetching && <span className='spinner-border spinner-border-sm' aria-hidden='true' />}
        {isFetching ? 'Refreshing...' : 'Refresh'}
      </button>
    </form>
  );
}

function ScheduleModal({ show, scheduleForm, onScheduleFormChange, assignees, onSchedule, onCancel, isScheduling, selectedCount }) {
  const [showAssigneePicker, setShowAssigneePicker] = useState(false);
  const assignedUser = assignees.find((assignee) => assignee.id === scheduleForm.assigneeUserId);

  const handleAssigneeChange = (event) => {
    onScheduleFormChange(event);
    setShowAssigneePicker(false);
  };

  return (
    <Modal show={show} onHide={onCancel} centered className='visits-map-modal'>
      <Modal.Header closeButton className='border-0 pb-0'>
        <Modal.Title className='visits-map-modal__title'>
          Create {selectedCount} Visit{selectedCount === 1 ? '' : 's'}
        </Modal.Title>
      </Modal.Header>
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          onSchedule();
        }}
      >
        <Modal.Body>
          <div className='d-flex gap-3 align-items-end mb-3'>
            <Form.Group className='flex-grow-1'>
              <Form.Label className='visits-map-modal__label'>Start Date for New Visits</Form.Label>
              <Form.Control
                type='date'
                name='date'
                value={scheduleForm.date}
                onChange={onScheduleFormChange}
                required
              />
            </Form.Group>
            <div className='d-flex align-items-center gap-2'>
              <span className={assignedUser ? '' : 'text-muted fst-italic'}>
                {assignedUser ? assignedUser.name : 'No users are currently assigned'}
              </span>
              <button
                type='button'
                className='visits-map-modal__assign-add'
                aria-label='Assign a user'
                onClick={() => setShowAssigneePicker((current) => !current)}
              >
                +
              </button>
            </div>
          </div>
          {showAssigneePicker && (
            <Form.Group className='mb-3'>
              <Form.Select
                name='assigneeUserId'
                value={scheduleForm.assigneeUserId}
                onChange={handleAssigneeChange}
                autoFocus
              >
                <option value=''>Unassigned</option>
                {assignees.map((assignee) => (
                  <option key={assignee.id} value={assignee.id}>{assignee.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          <Form.Group>
            <Form.Label className='visits-map-modal__label'>Start time (optional)</Form.Label>
            <Form.Control
              type='time'
              name='time'
              value={scheduleForm.time}
              onChange={onScheduleFormChange}
              className='visits-map-modal__time'
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className='border-0'>
          <Button variant='success' type='submit' disabled={isScheduling || !scheduleForm.date}>
            {isScheduling ? 'Scheduling...' : "Let's make some visits!"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function ScheduleResults({ results, jobById }) {
  if (!results) return null;

  const scheduled = results.filter((result) => result.status === 'scheduled');
  const skipped = results.filter((result) => result.status === 'skipped');
  const errored = results.filter((result) => result.status === 'error');

  return (
    <div className='p-3 pb-0' role='status'>
      <p className='mb-1'>
        Scheduled {scheduled.length}, skipped {skipped.length}, failed {errored.length}.
      </p>
      <ul className='list-unstyled mb-0 small'>
        {[...skipped, ...errored].map((result) => {
          const job = jobById.get(result.jobId);
          return (
            <li key={`${result.jobId}-${result.status}`} className={result.status === 'error' ? 'text-danger' : 'text-muted'}>
              {job ? `${job.client?.name} - #${job.jobNumber}` : result.jobId}: {result.reason}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function VisitsMap() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedParams, setAppliedParams] = useState(() => buildFilterParams(DEFAULT_FILTERS));
  const [selection, setSelection] = useState(() => ({
    mode: 'partial',
    ids: EMPTY_JOB_ID_SET,
  }));
  const [scheduleForm, setScheduleForm] = useState({ date: '', time: '', assigneeUserId: '' });
  const [scheduleResults, setScheduleResults] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const queryClient = useQueryClient();

  const handleScheduleFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setScheduleForm((currentForm) => ({ ...currentForm, [name]: value }));
  }, []);

  const { data, error, isError, isFetching, refetch } = useQuery({
    queryKey: ['visits-map', appliedParams],
    queryFn: ({ signal }) => apiGet(`visits-map${appliedParams ? `?${appliedParams}` : ''}`, { signal }),
    select: (responseData) => ({
      timezone: responseData.timezone,
      jobs: Array.isArray(responseData.jobs) ? responseData.jobs : [],
    }),
    retry: false,
  });

  const { data: assigneeData } = useQuery({
    queryKey: ['visits-map-assignees'],
    queryFn: () => apiGet('visits-map/assignees'),
    select: (responseData) => (Array.isArray(responseData.users) ? responseData.users : []),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const { data: filterOptions } = useQuery({
    queryKey: ['visits-map-filter-options'],
    queryFn: () => apiGet('visits-map/filter-options'),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Jobs without usable coordinates are skipped entirely so the list and
  // the map show the same jobs.
  const jobs = useMemo(() => (data?.jobs || []).filter((job) => {
    const lat = Number(job.property?.address?.coordinates?.latitude);
    const lng = Number(job.property?.address?.coordinates?.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng);
  }), [data]);
  const lineItemOptions = useMemo(() => collectLineItemNames(jobs), [jobs]);
  const assignees = assigneeData || [];

  const applyFilters = useCallback((nextFilters) => {
    setFilters(nextFilters);
    setAppliedParams(buildFilterParams(nextFilters));
    setScheduleResults(null);
  }, []);

  const markerPositions = useMemo(() => {
    return jobs.map((job) => ({
      lat: Number(job.property?.address?.coordinates?.latitude),
      lng: Number(job.property?.address?.coordinates?.longitude),
      id: job.jobId,
      clientName: job.client?.name || 'Unnamed client'
    })).filter(({ lat, lng }) => Number.isFinite(lat) && Number.isFinite(lng)) || [];
  }, [jobs]);

  const selectableJobIds = useMemo(() => {
    return Array.from(new Set(jobs.map((job) => job.jobId).filter(Boolean)));
  }, [jobs]);

  const selectableJobIdSet = useMemo(() => new Set(selectableJobIds), [selectableJobIds]);

  const jobById = useMemo(() => new Map(jobs.map((job) => [job.jobId, job])), [jobs]);

  const selectedJobIds = useMemo(() => {
    if (selection.mode === 'all') {
      return selectableJobIds.filter((jobId) => !selection.ids.has(jobId));
    }

    return selectableJobIds.filter((jobId) => selection.ids.has(jobId));
  }, [selectableJobIds, selection]);

  const selectedCount = selectedJobIds.length;

  const styledJobIdSet = selection.ids;
  const styledJobClassName = selection.mode === 'all' ? 'is-deselected' : 'is-selected';
  const isAllMode = selection.mode === 'all';

  const toggleSelectedJob = useCallback((jobId) => {
    setSelection((currentSelection) => {
      const nextIds = new Set(currentSelection.ids);

      if (nextIds.has(jobId)) {
        nextIds.delete(jobId);
      } else {
        nextIds.add(jobId);
      }

      return {
        mode: currentSelection.mode,
        ids: nextIds.size > 0 ? nextIds : EMPTY_JOB_ID_SET,
      };
    });
  }, []);

  const selectAllJobs = useCallback(() => {
    setSelection((currentSelection) => {
      if (currentSelection.mode === 'all' && currentSelection.ids.size === 0) {
        return currentSelection;
      }

      return {
        mode: 'all',
        ids: EMPTY_JOB_ID_SET,
      };
    });
  }, []);

  const clearSelectedJobs = useCallback(() => {
    setSelection((currentSelection) => {
      if (currentSelection.mode === 'partial' && currentSelection.ids.size === 0) {
        return currentSelection;
      }

      return {
        mode: 'partial',
        ids: EMPTY_JOB_ID_SET,
      };
    });
  }, []);

  const scheduleMutation = useMutation({
    mutationFn: (payload) => apiPost('visits-map/schedule', payload),
    onSuccess: (response) => {
      setScheduleResults(response.results || []);
      queryClient.invalidateQueries({ queryKey: ['visits-map'] });
    },
    onError: (scheduleError, payload) => {
      setScheduleResults(payload.jobIds.map((jobId) => ({
        jobId,
        status: 'error',
        reason: scheduleError?.message || 'Scheduling failed',
      })));
    },
    onSettled: () => {
      setShowScheduleModal(false);
    },
  });

  const handleSchedule = useCallback(() => {
    if (selectedJobIds.length === 0 || !scheduleForm.date) return;

    const assignee = assignees.find((entry) => entry.id === scheduleForm.assigneeUserId);

    scheduleMutation.mutate({
      jobIds: selectedJobIds,
      date: scheduleForm.date,
      time: scheduleForm.time || undefined,
      timezone: data?.timezone,
      assigneeUserId: scheduleForm.assigneeUserId || undefined,
      assigneeName: assignee?.name,
      horizon: filters.horizon,
    });
  }, [assignees, data?.timezone, filters.horizon, scheduleForm, scheduleMutation, selectedJobIds]);

  const mapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  if (isError) {
    return (
      <>
        <ResetSidebar />
        <div className="p-3">
          <p className="mb-3">{error?.message || "Unable to load visits map."}</p>
          <button onClick={() => refetch()}>Try again</button>
        </div>
      </>
    )
  }

  if (!mapsApiKey) {
    return (
      <>
        <ResetSidebar />
        <div className="p-3">
          <p>Google Maps is not configured. Set REACT_APP_GOOGLE_MAPS_API_KEY in the frontend environment.</p>
        </div>
      </>
    )
  }

  return (
    <>
      <ResetSidebar />
      <APIProvider apiKey={mapsApiKey}>
        <div className='visits-map d-flex flex-column'>
          <div className='visits-map-header d-flex justify-content-between align-items-start'>
            <div>
              <h1 className='visits-map-title'>Schedule New Visits</h1>
              <p className='visits-map-subtitle'>
                Quickly create visits for active <span className='visits-map-subtitle__accent'>recurring jobs</span>.
              </p>
            </div>
            {selectedCount > 0 && (
              <button
                type='button'
                className='btn btn-success visits-map-next-button'
                onClick={() => setShowScheduleModal(true)}
              >
                {selectedCount} Selected - Next
              </button>
            )}
          </div>
          <FilterBar
            filters={filters}
            options={filterOptions}
            lineItemOptions={lineItemOptions}
            onChange={setFilters}
            onApply={applyFilters}
            onRefresh={refetch}
            isFetching={isFetching}
          />
          <ScheduleResults results={scheduleResults} jobById={jobById} />
          {isFetching && jobs.length === 0 ? (
            <div className='d-flex align-items-center gap-2 p-3' role='status'>
              <span className='spinner-border spinner-border-sm' aria-hidden='true' />
              Loading jobs...
            </div>
          ) : (
            <div className={`visits-map-content d-flex mb-4 ${isAllMode ? 'is-all-selected' : ''}`}>
              <JobsList
                jobs={jobs}
                styledJobIdSet={styledJobIdSet}
                styledJobClassName={styledJobClassName}
                selectedCount={selectedCount}
                selectableJobCount={selectableJobIds.length}
                onToggleJob={toggleSelectedJob}
                onSelectAllJobs={selectAllJobs}
                onClearSelectedJobs={clearSelectedJobs}
              />
              <div className='visits-map-map'>
                <JobsMap
                  markerPositions={markerPositions}
                  styledJobIdSet={styledJobIdSet}
                  styledJobClassName={styledJobClassName}
                  onToggleJob={toggleSelectedJob}
                />
              </div>
            </div>
          )}
        </div>
      </APIProvider>
      <ScheduleModal
        show={showScheduleModal}
        scheduleForm={scheduleForm}
        onScheduleFormChange={handleScheduleFormChange}
        assignees={assignees}
        onSchedule={handleSchedule}
        onCancel={() => setShowScheduleModal(false)}
        isScheduling={scheduleMutation.isPending}
        selectedCount={selectedCount}
      />
    </>
  )
}

export default VisitsMap
